import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

const clearExternalCookie = (url) =>
  new Promise((resolve) => {
    if (!url || typeof Image === 'undefined') {
      resolve()
      return
    }

    const image = new Image()
    const timeout = window.setTimeout(resolve, 1500)
    const done = () => {
      window.clearTimeout(timeout)
      resolve()
    }
    image.onload = done
    image.onerror = done
    image.src = url
  })

/**
 * Authentication hook using TanStack Query
 *
 * Provides:
 * - user: Current user object (null if not authenticated)
 * - isLoading: True while checking auth status
 * - isAuthenticated: True if user is logged in
 * - logout: Function to log out and clear auth state
 * - refetch: Function to re-check auth status
 *
 * Usage:
 * ```jsx
 * const { user, isLoading, isAuthenticated, logout } = useAuth()
 *
 * if (isLoading) return <Loading />
 * if (!isAuthenticated) return <LoginPage />
 * return <Dashboard user={user} />
 * ```
 */
export function useAuth() {
  const queryClient = useQueryClient()

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const response = await api.get('/auth/me')
        return response
      } catch (err) {
        // 401 means not authenticated - this is expected, not an error
        if (err.message?.includes('401') || err.message?.includes('Not authenticated')) {
          return null
        }
        throw err
      }
    },
    retry: false, // Don't retry auth checks
    staleTime: 5 * 60 * 1000, // Consider auth fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  })

  const logout = async () => {
    try {
      const response = await api.post('/auth/logout')
      await clearExternalCookie(response?.pgBossDashboardLogoutUrl)
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      // Clear auth cache regardless of logout API result
      queryClient.setQueryData(['auth', 'me'], null)
      queryClient.invalidateQueries({ queryKey: ['auth'] })
    }
  }

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    error,
    logout,
    refetch,
  }
}
